import { useSyncExternalStore } from "react";

// Counter only — no `document`, no `window`: this module is imported by the server render too
// (apps/next, apps/cli). The diagnostic snapshot lives in AppBusyDebug.
// A forgotten release is caught by the e2e busyInvariant fixture, not by a watchdog in production.

export interface BusyHold {
	release(): void;
}

/**
 * A kind is a shared category, not an identity: two call sites holding "save" at once is the
 * ref-counting working as intended (the counter reaches 2 and drains back to 0). What the closed
 * union buys over a bare string is the case that has no good outcome — a typo, or two unrelated
 * features independently picking the same word — where one feature's leak would be attributed to
 * the other. Adding a kind is a deliberate edit here.
 */
export type BusyKind = "save" | "sync" | "navigation" | "clone" | "editor-init" | "scroll-restore" | "source-auth";

type KindState = { count: number; created: number };

const kinds = new Map<BusyKind, KindState>();
const listeners = new Set<() => void>();
let totalCount = 0;
let totalCreated = 0;

const kindState = (kind: BusyKind): KindState => {
	const existing = kinds.get(kind);
	if (existing) return existing;
	const state: KindState = { count: 0, created: 0 };
	kinds.set(kind, state);
	return state;
};

const notify = () => listeners.forEach((listener) => listener());

const hold = (kind: BusyKind): BusyHold => {
	const state = kindState(kind);
	let released = false;
	state.count++;
	state.created++;
	totalCount++;
	totalCreated++;
	notify();

	return {
		release() {
			if (released) throw new Error(`AppBusy: hold("${kind}") released more than once`);
			released = true;
			state.count--;
			totalCount--;
			notify();
		},
	};
};

const run = async <T>(kind: BusyKind, fn: () => Promise<T>): Promise<T> => {
	const busyHold = hold(kind);
	try {
		return await fn();
	} finally {
		busyHold.release();
	}
};

/** Side-effect free: getSnapshot calls it on every render. */
const isBusy = (kind: BusyKind): boolean => (kinds.get(kind)?.count ?? 0) > 0;

export const subscribe = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export interface AppBusySnapshot {
	busy: boolean;
	/** Currently held, per kind. */
	counts: Record<string, number>;
	/** Cumulative holds created since page load, per kind — e2e asserts deltas on these. */
	created: Record<string, number>;
	totalCreated: number;
}

export const snapshot = (): AppBusySnapshot => ({
	busy: totalCount > 0,
	counts: Object.fromEntries([...kinds].map(([kind, state]) => [kind, state.count])),
	created: Object.fromEntries([...kinds].map(([kind, state]) => [kind, state.created])),
	totalCreated,
});

const AppBusy = { hold, run };
export default AppBusy;

// The subscription is global, so getSnapshot runs on any counter change — but React compares
// snapshots with Object.is, and this one is a boolean, so an unrelated kind causes no re-render.
export const useBusy = (kind: BusyKind): boolean =>
	useSyncExternalStore(
		subscribe,
		() => isBusy(kind),
		() => false,
	);
